import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTaskInput, ListTasksQuery, TaskCountsResponse, TaskResponse, UpdateTaskInput } from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListTasksQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useTasksQuery(query: Partial<ListTasksQuery>) {
  return useQuery({
    queryKey: ['tasks', query],
    queryFn: () => getPage<TaskResponse[]>(`/tasks${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useTaskCountsQuery() {
  return useQuery({ queryKey: ['tasks', 'counts'], queryFn: () => api.get<TaskCountsResponse>('/tasks/counts') });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<TaskResponse>('/tasks', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskInput }) => api.patch<TaskResponse>(`/tasks/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useTaskQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: () => api.get<TaskResponse>(`/tasks/${id}`),
    enabled: Boolean(id),
  });
}
